
import { useState } from "react";
import { Star } from "lucide-react";
import { cn } from "@/lib/utils";

interface StarRatingProps {
  value?: number;
  onChange?: (rating: number) => void;
  readOnly?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
}

export const StarRating = ({
  value = 0,
  onChange,
  readOnly = false,
  size = "sm",
  className,
}: StarRatingProps) => {
  const [hoverRating, setHoverRating] = useState<number | null>(null);
  const [animateIndex, setAnimateIndex] = useState<number | null>(null);
  
  const sizeMap = {
    sm: { starSize: 16, className: "gap-0.5" },
    md: { starSize: 20, className: "gap-1" },
    lg: { starSize: 24, className: "gap-1.5" },
  };
  
  const handleRatingChange = (rating: number) => {
    if (readOnly) return;
    
    onChange?.(rating);
    
    // Small animation when user clicks a star
    setAnimateIndex(rating - 1);
    setTimeout(() => setAnimateIndex(null), 300);
  };

  return (
    <div 
      className={cn("flex items-center", sizeMap[size].className, className)}
      onMouseLeave={() => !readOnly && setHoverRating(null)}
    >
      {[1, 2, 3, 4, 5].map((rating) => {
        const isFilled = (hoverRating !== null ? rating <= hoverRating : rating <= Math.floor(value));
        const isHalfFilled = hoverRating === null && rating === Math.ceil(value) && value % 1 !== 0;
        
        return (
          <div 
            key={rating} 
            className={cn(
              "relative cursor-default transition-transform",
              !readOnly && "cursor-pointer hover:scale-110",
              animateIndex === rating - 1 && "animate-bounce"
            )}
            onMouseEnter={() => !readOnly && setHoverRating(rating)}
            onClick={() => handleRatingChange(rating)}
          >
            <Star
              size={sizeMap[size].starSize}
              className={cn(
                "transition-colors",
                isFilled ? "text-yellow-400 fill-yellow-400" : "text-gray-300",
              )}
            />
            
            {isHalfFilled && (
              <div className="absolute inset-0 overflow-hidden w-1/2">
                <Star
                  size={sizeMap[size].starSize}
                  className="text-yellow-400 fill-yellow-400"
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
